import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ErrorMessages,
  ErrorRow,
  ErrorRowNumber,
  ErrorTable,
  HelpPrompt,
  HelpPromptButton,
  HelpPromptText,
  ResultBox,
  ResultSummary,
  ResultTitle,
  Template,
} from './ImportModal.styles';
import ImportParticipantsAdaptPulseHelpModal from './ImportParticipantsAdaptPulseHelpModal';
import ImportParticipantsEffectHelpModal from './ImportParticipantsEffectHelpModal';
import ImportReportAccessHelpModal from './ImportReportAccessHelpModal';
import ImportReportingGroupsHelpModal from './ImportReportingGroupsHelpModal';
import Modal from '../../HOC/Modal/Modal';
import {
  downloadImportTemplate,
  IImportResult,
  ImportKind,
  postImport,
} from '../../services/imports';
import Dropzone from '../Dropzone/Dropzone';
interface IImportModal {
  active: boolean;
  kind: ImportKind;
  surveyId: string;
  onClose: () => void;
  /** Called after an import that changed data, so the caller can refresh its list. */
  onImported: () => Promise<void> | void;
  isEffectSurvey?: boolean;
  isAdaptPulseSurvey?: boolean;
}

type ImportHelpGuide =
  | 'reporting-groups'
  | 'participants-effect'
  | 'participants-adapt-pulse'
  | 'report-access';

const HELP_TRANSLATION_KEYS: Record<ImportHelpGuide, string> = {
  'reporting-groups': 'imports.reportingGroupsHelp',
  'participants-effect': 'imports.participantsHelp.effect',
  'participants-adapt-pulse': 'imports.participantsHelp.adaptPulse',
  'report-access': 'imports.reportAccessHelp',
};

const ImportModal: FC<IImportModal> = ({
  active,
  kind,
  surveyId,
  onClose,
  onImported,
  isEffectSurvey = false,
  isAdaptPulseSurvey = false,
}) => {
  const { t } = useTranslation();

  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<IImportResult | null>(null);
  const [pending, setPending] = useState(false);
  const [helpActive, setHelpActive] = useState(false);

  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    setPending(false);
    setHelpActive(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFileChange = useCallback((next: File | null) => {
    setFile(next);
    // A previous run's outcome does not describe the newly picked file.
    setResult(null);
  }, []);

  const handleImport = useCallback(async () => {
    if (!file) return;

    setPending(true);
    try {
      const importResult = await postImport(kind, surveyId, file);
      setResult(importResult);

      // Even a partial import commits rows, so always refresh.
      if (importResult.status === 'success') {
        await onImported();
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setPending(false);
    }
  }, [file, kind, surveyId, onImported]);

  const handleTemplateDownload = useCallback(
    async (fileFormat: 'csv' | 'xlsx') => {
      try {
        await downloadImportTemplate(kind, surveyId, fileFormat);
      } catch (error) {
        console.log({ error });
      }
    },
    [kind, surveyId]
  );

  const succeeded = result?.status === 'success';
  const hasRowErrors = !!result?.rowErrors?.length;
  let helpGuide: ImportHelpGuide | null = null;

  if (kind === 'reportingGroups') {
    helpGuide = 'reporting-groups';
  } else if (kind === 'users') {
    helpGuide = 'report-access';
  } else if (isEffectSurvey) {
    helpGuide = 'participants-effect';
  } else if (isAdaptPulseSurvey) {
    helpGuide = 'participants-adapt-pulse';
  }

  return (
    <>
      <Modal
        active={active}
        callToAction={handleImport}
        closeModal={handleClose}
        buttonNames={{
          cancel: t('imports.close'),
          action: t('imports.action'),
        }}
        width="560px"
        top={'20vh'}
        title={t(`imports.title.${kind}`)}
        noC2AButton={!file || succeeded}
        loading={pending}
      >
        <div>
          {helpGuide ? (
            <HelpPrompt data-testid={`import-${helpGuide}-help-prompt`}>
              <HelpPromptText>{t(`${HELP_TRANSLATION_KEYS[helpGuide]}.prompt`)}</HelpPromptText>
              <HelpPromptButton
                type="button"
                data-testid={`import-${helpGuide}-help-link`}
                onClick={() => setHelpActive(true)}
              >
                {t(`${HELP_TRANSLATION_KEYS[helpGuide]}.link`)}
              </HelpPromptButton>
            </HelpPrompt>
          ) : null}
          <Dropzone file={file} onFileChange={handleFileChange} disabled={pending} />

          {result && (
            <>
              <ResultBox mode={succeeded ? 'success' : 'error'} data-testid="import-result">
                <ResultTitle>
                  {succeeded ? t('imports.result.success') : t('imports.result.failed')}
                </ResultTitle>
                <ResultSummary>
                  {t('imports.result.summary', {
                    created: result.summary.created,
                    updated: result.summary.updated,
                    skipped: result.summary.skipped,
                  })}
                </ResultSummary>
              </ResultBox>

              {hasRowErrors && (
                <ErrorTable data-testid="import-row-errors">
                  {result.rowErrors.map((rowError, index) => (
                    <ErrorRow key={`${rowError.row}-${index}`}>
                      <ErrorRowNumber>
                        {rowError.row === null
                          ? t('imports.result.file')
                          : t('imports.result.row', { row: rowError.row })}
                      </ErrorRowNumber>
                      <ErrorMessages>{rowError.errors.join('\n')}</ErrorMessages>
                    </ErrorRow>
                  ))}
                </ErrorTable>
              )}
            </>
          )}

          <Template>
            <b>{t('imports.template.question')}</b> {t(`imports.template.get.${kind}`)}
            <button type="button" onClick={() => handleTemplateDownload('xlsx')}>
              {t('imports.template.xlsx')}
            </button>
            {' / '}
            <button type="button" onClick={() => handleTemplateDownload('csv')}>
              {t('imports.template.csv')}
            </button>
          </Template>
        </div>
      </Modal>
      {helpActive && helpGuide === 'reporting-groups' ? (
        <ImportReportingGroupsHelpModal
          active
          onClose={() => setHelpActive(false)}
          variant={isEffectSurvey ? 'effect' : 'adaptPulse'}
        />
      ) : null}
      {helpActive && helpGuide === 'participants-effect' ? (
        <ImportParticipantsEffectHelpModal active onClose={() => setHelpActive(false)} />
      ) : null}
      {helpActive && helpGuide === 'participants-adapt-pulse' ? (
        <ImportParticipantsAdaptPulseHelpModal active onClose={() => setHelpActive(false)} />
      ) : null}
      {helpActive && helpGuide === 'report-access' ? (
        <ImportReportAccessHelpModal active onClose={() => setHelpActive(false)} />
      ) : null}
    </>
  );
};

export default ImportModal;
